/**
 * GlobalTooltip — tooltip stylé unique, monté une seule fois à la racine de l'app.
 *
 * Écoute au niveau du document (délégation) et affiche un tooltip pour TOUT
 * élément interactif portant un libellé accessible (`data-tooltip`, `aria-label`
 * ou `title`), au survol ET au focus clavier. Remplace les tooltips natifs
 * (`title`) moches et lents : l'attribut `title` est retiré au survol (puis
 * restauré) pour éviter le doublon avec le tooltip natif du navigateur.
 *
 * Anti-bruit : si le libellé est déjà visible dans le texte de l'élément
 * (bouton textué), on n'affiche pas de tooltip — il n'apporterait rien. Les
 * boutons-icônes (sans texte visible) obtiennent donc leur tooltip, pas les
 * boutons déjà légendés.
 */
import { useEffect, useRef, useState } from 'react'

const OPEN_DELAY = 350
/** Largeur max du tooltip (px). Sert au wrap du texte ET au clamp horizontal. */
const TIP_MAX_W = 240
/** Marge minimale avec les bords de la fenêtre (px). */
const EDGE_MARGIN = 8
/** Sélecteur des porteurs de libellé qu'on transforme en tooltip. */
const LABEL_SELECTOR = '[data-tooltip],[aria-label],[title]'

interface TipState {
  text: string
  x: number
  y: number
  placement: 'top' | 'bottom'
}

/** Libellé accessible d'un élément, par ordre de priorité. */
function labelOf(el: HTMLElement): string {
  return (
    el.getAttribute('data-tooltip') ||
    el.getAttribute('aria-label') ||
    el.getAttribute('title') ||
    ''
  ).trim()
}

/** Le libellé est-il déjà rendu visible dans l'élément ? (évite les tooltips redondants) */
function labelIsRedundant(el: HTMLElement, label: string): boolean {
  const visible = (el.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase()
  if (!visible) return false
  return visible === label.toLowerCase() || visible.includes(label.toLowerCase())
}

export default function GlobalTooltip() {
  const [tip, setTip] = useState<TipState | null>(null)
  const timer = useRef<number | null>(null)
  /** Élément dont on a retiré le `title` natif (à restaurer à la sortie). */
  const stripped = useRef<{ el: HTMLElement; title: string } | null>(null)

  useEffect(() => {
    function clearTimer() {
      if (timer.current !== null) {
        window.clearTimeout(timer.current)
        timer.current = null
      }
    }

    function restoreTitle() {
      if (stripped.current) {
        stripped.current.el.setAttribute('title', stripped.current.title)
        stripped.current = null
      }
    }

    function show(el: HTMLElement) {
      const label = labelOf(el)
      if (!label || labelIsRedundant(el, label)) return

      // Supprime le title natif le temps de l'affichage (anti-doublon).
      const nativeTitle = el.getAttribute('title')
      if (nativeTitle) {
        restoreTitle()
        stripped.current = { el, title: nativeTitle }
        el.removeAttribute('title')
      }

      const rect = el.getBoundingClientRect()
      const placement: TipState['placement'] = rect.top < 44 ? 'bottom' : 'top'
      // Clamp horizontal : garde le tooltip (centré, largeur max TIP_MAX_W) dans la
      // fenêtre pour qu'un libellé long ne déborde jamais d'un bord.
      const half = TIP_MAX_W / 2
      const rawX = rect.left + rect.width / 2
      const maxX = window.innerWidth - EDGE_MARGIN - half
      const minX = EDGE_MARGIN + half
      const x = maxX < minX ? window.innerWidth / 2 : Math.max(minX, Math.min(rawX, maxX))
      setTip({
        text: label,
        x,
        y: placement === 'top' ? rect.top : rect.bottom,
        placement,
      })
    }

    function hide() {
      clearTimer()
      restoreTitle()
      setTip(null)
    }

    function onEnter(e: Event) {
      const target = e.target as HTMLElement | null
      const el = target?.closest?.(LABEL_SELECTOR) as HTMLElement | null
      if (!el) return
      clearTimer()
      timer.current = window.setTimeout(() => show(el), OPEN_DELAY)
    }

    function onLeave(e: Event) {
      const target = e.target as HTMLElement | null
      const el = target?.closest?.(LABEL_SELECTOR) as HTMLElement | null
      if (!el) return
      hide()
    }

    // mouseover/out remontent (delegation) ; focusin/out gèrent le clavier.
    document.addEventListener('mouseover', onEnter, true)
    document.addEventListener('mouseout', onLeave, true)
    document.addEventListener('focusin', onEnter, true)
    document.addEventListener('focusout', onLeave, true)
    // Un scroll/clic ferme le tooltip ouvert (position devient obsolète).
    window.addEventListener('scroll', hide, true)
    document.addEventListener('click', hide, true)

    return () => {
      clearTimer()
      restoreTitle()
      document.removeEventListener('mouseover', onEnter, true)
      document.removeEventListener('mouseout', onLeave, true)
      document.removeEventListener('focusin', onEnter, true)
      document.removeEventListener('focusout', onLeave, true)
      window.removeEventListener('scroll', hide, true)
      document.removeEventListener('click', hide, true)
    }
  }, [])

  if (!tip) return null

  const OFFSET = 8
  return (
    <div
      role="tooltip"
      style={{
        position: 'fixed',
        left: tip.x,
        top: tip.placement === 'top' ? tip.y - OFFSET : tip.y + OFFSET,
        transform: `translate(-50%, ${tip.placement === 'top' ? '-100%' : '0'})`,
        zIndex: 9999,
        pointerEvents: 'none',
        maxWidth: TIP_MAX_W,
      }}
      className="rounded-md bg-[#1B2559] px-2 py-1 text-xs font-medium leading-snug text-white shadow-lg break-words"
    >
      {tip.text}
    </div>
  )
}
